/**
 * Canonical localStorage key prefix + one-shot migration from the legacy
 * `senso_` prefix.
 *
 * The Sensar rebrand was a word-boundary rename — `senso` followed by `_`
 * doesn't match `\bsenso\b` (because `_` is a word char), so these eight
 * keys were missed. New deployments don't care which prefix is used; the
 * problem is fielded devices already wrote data under the old keys.
 *
 * `migrateLegacyStorageKeys()` runs once at boot (called from main.tsx)
 * and copies any remaining senso_* values to sensar_*, then deletes the
 * old keys. Idempotent — repeat calls are no-ops once migrated.
 */

const PREFIX = 'sensar_';
const LEGACY_PREFIX = 'senso_';

const KEY_SUFFIXES = [
  'pairing_code',
  'board_id',
  'board_code',
  'cached_plan',
  'plan_version',
  'proof_of_play_queue',
  'presence_queue',
  'last_sync',
] as const;

export type StorageKeySuffix = (typeof KEY_SUFFIXES)[number];

export function storageKey(suffix: StorageKeySuffix): string {
  return PREFIX + suffix;
}

export function migrateLegacyStorageKeys(): void {
  if (typeof localStorage === 'undefined') return;
  for (const suffix of KEY_SUFFIXES) {
    const newKey = PREFIX + suffix;
    const oldKey = LEGACY_PREFIX + suffix;
    if (localStorage.getItem(newKey) !== null) continue;
    const legacy = localStorage.getItem(oldKey);
    if (legacy === null) continue;
    localStorage.setItem(newKey, legacy);
    localStorage.removeItem(oldKey);
  }
}
